/** Display formatting. Kept in one place so durations look the same everywhere. */

/** `3:07`, or `1:02:33` for anything over an hour. */
export function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '0:00'
  const seconds = Math.floor(totalSeconds % 60)
  const minutes = Math.floor((totalSeconds / 60) % 60)
  const hours = Math.floor(totalSeconds / 3600)
  const paddedSeconds = seconds.toString().padStart(2, '0')
  return hours > 0
    ? `${hours}:${minutes.toString().padStart(2, '0')}:${paddedSeconds}`
    : `${minutes}:${paddedSeconds}`
}

export function formatMs(milliseconds: number): string {
  return formatDuration(milliseconds / 1000)
}

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB']

export function formatBytes(bytes: number): string {
  if (!bytes) return '0 B'
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), UNITS.length - 1)
  const value = bytes / 1024 ** exponent
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${UNITS[exponent]}`
}

export function formatSpeed(bytesPerSecond: number | null): string {
  if (!bytesPerSecond) return ''
  return `${formatBytes(bytesPerSecond)}/s`
}

export function formatEta(seconds: number | null): string {
  if (!seconds || seconds <= 0) return ''
  if (seconds < 60) return `${Math.round(seconds)}s left`
  return `${Math.round(seconds / 60)}m left`
}

/** `Today`, `Yesterday`, `12 Mar`, or `12 Mar 2024` for other years. */
export function formatDate(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  const now = new Date()
  // Calendar days apart, not elapsed hours. Dividing the millisecond delta by a
  // day said 23:50 last night was "Today" when read at 00:10, because it was
  // twenty minutes ago — and on a listening history the day is the whole label.
  const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const days = Math.round((midnight(now) - midnight(date)) / 86_400_000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  const sameYear = date.getFullYear() === now.getFullYear()
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

const STAGE_LABELS: Record<string, string> = {
  resolving: 'Looking up track',
  downloading: 'Downloading',
  converting: 'Converting audio',
  tagging: 'Writing tags',
  finalizing: 'Finishing up',
}

export function stageLabel(stage: string): string {
  return STAGE_LABELS[stage] ?? 'Working'
}
